import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Lock, Star, CheckCircle2, AlertTriangle } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { openRazorpayCheckout } from "../../lib/razorpay";
import { API } from "./shared";

// ============================================================
// VIEW: DIRECTORY (Design and Creative Supplier Network)
// ============================================================
const AL_COLS = "1.5fr 1.4fr 1.3fr .9fr 150px";
const AL = { ink: "#134E4A", teal: "#0F766E", soft: "#F0FDFA", line: "#CCFBF1" };

function BuyerAgencyList({ suppliers, rateEntries, specLabel, tierLabel, onHire, onView }) {
  if (!suppliers.length) return <div className="pipeline-empty" data-testid="agency-list-empty">No other agencies match your search.</div>;
  return (
    <div data-testid="agency-list" style={{marginTop:18,border:`1px solid ${AL.line}`,borderRadius:14,overflow:"hidden",background:"#fff"}}>
      <div style={{padding:"12px 18px",background:AL.soft,borderBottom:`1px solid ${AL.line}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <b style={{color:AL.ink,font:"700 13px Outfit",letterSpacing:".02em"}}>More verified agencies</b>
        <span style={{fontSize:12,color:AL.teal}}>{suppliers.length} {suppliers.length === 1 ? "agency" : "agencies"}</span>
      </div>
      <div style={{display:"grid",gridTemplateColumns:AL_COLS,gap:14,padding:"10px 18px",background:AL.teal,color:"#fff",font:"700 10px 'DM Mono', monospace",letterSpacing:".12em"}}>
        <span>AGENCY</span><span>SPECIALTIES</span><span>PROJECT PRICES</span><span>RATING &amp; TIER</span><span style={{textAlign:"right"}}>ACTION</span>
      </div>
      {suppliers.map((s, i) => {
        const rates = rateEntries(s);
        const hireReady = rates.length > 0 && Number(s.revision_rate_inr) > 0;
        const rating = Number(s.quality_rating || 0);
        return (
          <div key={s.id} data-testid={`agency-row-${s.id}`} className="agency-list-row"
            style={{display:"grid",gridTemplateColumns:AL_COLS,gap:14,padding:"14px 18px",alignItems:"center",borderTop: i ? `1px solid ${AL.line}` : "none"}}>
            <div style={{display:"flex",gap:12,alignItems:"center",minWidth:0}}>
              <div style={{width:40,height:40,borderRadius:10,background:AL.soft,border:`1px solid ${AL.line}`,color:AL.teal,display:"flex",alignItems:"center",justifyContent:"center",font:"700 13px Outfit",flexShrink:0}}>
                {s.name.split(" ").map(y => y[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div style={{minWidth:0}}>
                <b style={{display:"block",color:"#0f172a",fontSize:14}}>{s.name}</b>
                <span style={{fontSize:11,color:"#64748b"}}>
                  {s.is_verified ? <span style={{color:"#15803D",fontWeight:700,display:"inline-flex",alignItems:"center",gap:3}}><CheckCircle2 size={10} style={{display:"inline"}}/>Verified</span> : (s.verification_stage || "Pending")} · {s.location?.city || "Remote"}
                </span>
              </div>
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:4}} data-testid={`agency-row-specs-${s.id}`}>
              {(s.design_specialties || []).length ? (s.design_specialties || []).slice(0, 3).map(c => (
                <span key={c} style={{fontSize:11,padding:"2px 8px",borderRadius:999,background:AL.soft,color:AL.ink,border:`1px solid ${AL.line}`}}>{specLabel(c)}</span>
              )) : <span style={{fontSize:12,color:"#94a3b8"}}>Multi-service</span>}
              {(s.design_specialties || []).length > 3 && <span style={{fontSize:11,color:"#64748b"}}>+{s.design_specialties.length - 3}</span>}
            </div>
            <div style={{fontSize:12}} data-testid={`agency-row-rates-${s.id}`}>
              {rates.length ? (
                <>
                  <b style={{color:"#065F46"}}>{rates.slice(0, 2).map(r => `${r.label} ₹${Number(r.inr).toLocaleString("en-IN")}`).join(" · ")}</b>
                  <span style={{display:"block",color:"#64748b",fontSize:11,marginTop:2}}>5 free revisions · {s.revision_rate_inr ? `then ₹${Number(s.revision_rate_inr).toLocaleString("en-IN")}/round` : "revision rate not set"}</span>
                </>
              ) : <span style={{color:"#92400E"}}>Quotes per project</span>}
            </div>
            <div style={{fontSize:12}} data-testid={`agency-row-rating-${s.id}`}>
              <span style={{color:"#F59E0B"}}>★</span> <b>{rating ? rating.toFixed(1) : "New"}</b>
              {s.rating_count > 0 && <small style={{color:"#64748b"}}> ({s.rating_count})</small>}
              {s.design_tier && <span style={{display:"flex",alignItems:"center",gap:3,marginTop:3,fontSize:10,fontWeight:700,letterSpacing:".06em",textTransform:"uppercase",color:AL.teal}}><ShieldCheck size={10} style={{display:"inline"}}/>{tierLabel(s.design_tier)}</span>}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end"}}>
              {s.is_verified && (
                <button data-testid={`request-hire-${s.id}`} onClick={() => onHire(s, rates)}
                  style={{background:AL.teal,color:"#fff",border:0,borderRadius:8,padding:"7px 12px",font:"600 12px Outfit",cursor:"pointer",whiteSpace:"nowrap",display:"inline-flex",gap:6,alignItems:"center",transition:"transform .15s, background-color .15s"}}>
                  {hireReady ? "Request hire" : "Request quote"} <Lock size={12}/>
                </button>
              )}
              <button data-testid={`supplier-view-${i}`} onClick={() => onView(s)}
                style={{background:"none",border:0,color:AL.teal,font:"600 12px Outfit",cursor:"pointer",textDecoration:"underline",padding:0}}>View profile</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const ath = { padding: "9px 12px", fontSize: 10.5, letterSpacing: ".06em", textTransform: "uppercase", color: "#64748b", fontWeight: 700, textAlign: "left", whiteSpace: "nowrap" };
const atd = { padding: "10px 12px", fontSize: 12.5, verticalAlign: "top", borderTop: "1px solid #EEF2F7" };
const asel = { border: "1px solid #CBD5E1", borderRadius: 6, padding: "4px 6px", fontSize: 12, maxWidth: 170 };

function AdminAgencyTable({ suppliers, stages, tiers, rateEntries, specLabel, tierLabel, setStage, setDesignTier, setFeatured, onView }) {
  return (
    <div data-testid="admin-agency-list" style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead style={{ background: "#F8FAFC" }}><tr>
          {["Agency", "Specialties", "Published rates", "Rating", "Verification", "Design tier", "Spotlight", ""].map(h => <th key={h} style={ath}>{h}</th>)}
        </tr></thead>
        <tbody>{suppliers.map((s, i) => {
          const rates = rateEntries(s);
          return (
            <tr key={s.id} data-testid={`supplier-card-${s.id}`} className="admin-design-row">
              <td style={atd}>
                <b style={{ color: "#0F172A" }}>{s.name}</b>
                {s.is_test_account && <span style={{ fontSize: 9.5, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: "#FEF3C7", color: "#92400E", marginLeft: 6 }}>TEST</span>}
                <div style={{ marginTop: 3 }}>
                  {s.is_verified
                    ? <span className="verify-pill ok" data-testid={`dir-verify-${s.id}`}><CheckCircle2 size={10}/> Verified</span>
                    : <span className="verify-pill pending" data-testid={`dir-verify-${s.id}`}><AlertTriangle size={10}/> {s.verification_stage || "Pending Documents"}</span>}
                </div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{s.email} · {s.location?.city || "Remote"}</div>
              </td>
              <td style={{ ...atd, maxWidth: 220 }}>{(s.design_specialties || []).map(specLabel).join(", ") || <span style={{ color: "#94a3b8" }}>—</span>}</td>
              <td style={{ ...atd, maxWidth: 240 }} data-testid={`rate-chips-${s.id}`}>
                {rates.length ? rates.map(r => <div key={r.code} style={{ whiteSpace: "nowrap" }}>{r.label} · ₹{r.inr.toLocaleString("en-IN")}</div>) : <span style={{ color: "#94a3b8" }}>Not published</span>}
                {s.revision_rate_inr ? <div style={{ color: "#1E3A8A", fontSize: 11.5 }}>Revision ₹{Number(s.revision_rate_inr).toLocaleString("en-IN")}/round</div> : null}
              </td>
              <td style={{ ...atd, whiteSpace: "nowrap" }} data-testid={`dir-stars-${s.id}`}>
                <span style={{ color: "#F59E0B" }}>★</span> {Number(s.quality_rating || 4.8).toFixed(1)}
                <small style={{ color: "#9CA3AF", marginLeft: 4 }}>{s.rating_count > 0 ? `(${s.rating_count})` : "· new"}</small>
              </td>
              <td style={atd}>
                <select data-testid={`verify-select-${s.id}`} value={s.verification_stage || "Pending Documents"} onChange={e => setStage(s.id, e.target.value)} style={asel}>
                  {stages.map(st => <option key={st} value={st}>{st}</option>)}
                </select>
              </td>
              <td style={atd}>
                <select data-testid={`dtier-select-${s.id}`} value={s.design_tier || ""} onChange={e => setDesignTier(s.id, e.target.value)} style={asel}>
                  <option value="">— not tagged —</option>
                  {tiers.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
                </select>
                {s.design_tier && <div data-testid={`dtier-badge-${s.id}`} style={{ fontSize: 11, color: "#475569", marginTop: 3 }}><ShieldCheck size={10} style={{ verticalAlign: -1 }}/> {tierLabel(s.design_tier)}</div>}
              </td>
              <td style={atd}>
                {s.is_verified ? (
                  <button type="button" data-testid={`feature-toggle-${s.id}`} onClick={() => setFeatured(s.id, !s.is_featured, s.name)}
                    title={s.is_featured ? "Click to remove this agency from the spotlight" : "Click to feature this agency at the top of the Supplier Network"}
                    style={{ padding: "5px 9px", borderRadius: 6, border: `1px solid ${s.is_featured ? "#F59E0B" : "#D1D5DB"}`, background: s.is_featured ? "#FEF3C7" : "#F9FAFB",
                      color: s.is_featured ? "#92400E" : "#374151", fontSize: 11, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
                    <Star size={11} fill={s.is_featured ? "#F59E0B" : "none"} strokeWidth={1.6}/>{s.is_featured ? "Featured · remove" : "Feature"}
                  </button>
                ) : <span style={{ fontSize: 11, color: "#94a3b8" }}>Verify first</span>}
              </td>
              <td style={atd}><button data-testid={`supplier-view-${i}`} onClick={() => onView(s)} style={{ border: 0, background: "none", color: "#1d4ed8", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", fontSize: 12.5 }}>View profile →</button></td>
            </tr>
          );
        })}</tbody>
      </table>
    </div>
  );
}

export function Directory({ role, onHireSuccess }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [stages, setStages] = useState([]);
  const [ref, setRef] = useState({ specialties: [], tiers: [] });
  const [quickHire, setQuickHire] = useState(null);
  const [profileFor, setProfileFor] = useState(null);
  const [sortBy, setSortBy] = useState("tier");
  const [search, setSearch] = useState("");
  const reload = () => axios.get(`${API}/users`).then(r => setUsers(r.data)).catch(() => {});
  useEffect(() => {
    reload();
    axios.get(`${API}/capabilities/reference`).then(r => setStages(r.data.verification_stages || [])).catch(() => {});
    axios.get(`${API}/design/reference`).then(r => setRef(r.data)).catch(() => {});
  }, []);
  // Lowest published fixed project price (used for sort-by-rate).
  const minRate = (u) => {
    const vals = Object.values(u.fixed_rates || {}).map(Number).filter(n => n > 0);
    return vals.length ? Math.min(...vals) : null;
  };
  const tierRank = (u) => {
    // Verified first, then design_tier (platinum > gold > silver > bronze), then pending.
    const order = { platinum: 4, gold: 3, silver: 2, bronze: 1 };
    if (!u.is_verified) return -1;
    return 10 + (order[u.design_tier] || 0);
  };
  const capabilityLabel = (u) => {
    if (u.role === "agency") return (u.design_specialties || [])[0] || "zzz";
    return (u.machine_capabilities || [])[0] || u.specialty || "zzz";
  };
  const specLabel = (code) => ref.specialties?.find(s => s.code === code)?.label || code;
  const tierLabel = (code) => ref.tiers?.find(t => t.code === code)?.label || code;
  const suppliersAll = users.filter(u => u.role === "agency");
  const q = search.trim().toLowerCase();
  const matchesSearch = (u) => {
    if (!q) return true;
    const specTokens = (u.design_specialties || []).flatMap(code => [code.toLowerCase(), specLabel(code).toLowerCase()]);
    const hay = [
      u.name || "",
      u.location?.city || "",
      u.specialty || "",
      tierLabel(u.design_tier || "").toString(),
      ...specTokens,
    ].join(" ").toLowerCase();
    return hay.includes(q);
  };
  const suppliersFiltered = suppliersAll.filter(matchesSearch);
  // Featured agency: admin can hand-pick via `is_featured` flag; fall back to the
  // top-rated verified agency with a published rate so the spotlight is never empty.
  // Only shown on the default view (no active search) so it does not fight the user's intent.
  const nominated = suppliersAll.find(u => u.is_featured && u.is_verified);
  const autoPick = [...suppliersAll]
    .filter(u => u.is_verified && (Object.values(u.fixed_rates || {}).some(v => Number(v) > 0)))
    .sort((a, b) => {
      const rb = Number(b.quality_rating) || 0;
      const ra = Number(a.quality_rating) || 0;
      if (rb !== ra) return rb - ra;
      return tierRank(b) - tierRank(a);
    })[0];
  const featured = (!q) ? (nominated || autoPick || null) : null;
  const featuredIsNominated = !!(featured && nominated && featured.id === nominated.id);
  const gridSource = featured ? suppliersFiltered.filter(u => u.id !== featured.id) : suppliersFiltered;
  const suppliers = [...gridSource].sort((a, b) => {
    switch (sortBy) {
      case "name_asc":
        return (a.name || "").localeCompare(b.name || "");
      case "name_desc":
        return (b.name || "").localeCompare(a.name || "");
      case "recent": {
        const ta = new Date(a.created_at || 0).getTime();
        const tb = new Date(b.created_at || 0).getTime();
        return tb - ta;
      }
      case "rate_asc": {
        const ra = minRate(a); const rb = minRate(b);
        if (ra === null && rb === null) return (a.name || "").localeCompare(b.name || "");
        if (ra === null) return 1;
        if (rb === null) return -1;
        return ra - rb;
      }
      case "rate_desc": {
        const ra = minRate(a); const rb = minRate(b);
        if (ra === null && rb === null) return (a.name || "").localeCompare(b.name || "");
        if (ra === null) return 1;
        if (rb === null) return -1;
        return rb - ra;
      }
      case "capability":
        return capabilityLabel(a).localeCompare(capabilityLabel(b)) || (a.name || "").localeCompare(b.name || "");
      case "tier":
      default:
        return (tierRank(b) - tierRank(a)) || (a.name || "").localeCompare(b.name || "");
    }
  });
  const setStage = async (id, stage) => {
    await axios.patch(`${API}/users/${id}/verification`, { verification_stage: stage });
    reload();
  };
  const setDesignTier = async (id, tier) => {
    if (!tier) return;
    await axios.patch(`${API}/admin/users/${id}/design-tier`, { tier });
    reload();
  };
  const setFeatured = async (id, next, name) => {
    try {
      await axios.patch(`${API}/admin/users/${id}/featured`, { featured: !!next });
      toast.success(next ? `${name} is now the featured agency` : `${name} removed from spotlight`, {
        description: next ? "They now appear at the top of the Supplier Network for every buyer." : "Spotlight falls back to the top-rated verified agency.",
      });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update featured status.");
    }
  };
  // Agency rate helpers
  const rateEntries = (agency) => {
    const out = [];
    const fr = agency.fixed_rates || {};
    for (const code of agency.design_specialties || []) {
      if (fr[code]) out.push({ code, label: specLabel(code), inr: fr[code] });
    }
    return out;
  };
  return (
    <section className="content">
      <div className="section-heading" style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:16,flexWrap:"wrap"}}>
        <div>
          <p className="eyebrow">VERIFIED NETWORK</p>
          <h2>Design and Creative Supplier Network</h2>
          <p className="subhead">A qualified regional network of printing and packaging design agencies. Rates below are the agencies' own published fixed prices — hire with one click and the full amount is held in escrow until you approve the artwork.</p>
        </div>
        <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"flex-end"}}>
          <label style={{display:"flex",flexDirection:"column",gap:4,fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"0.05em",textTransform:"uppercase",minWidth:260}}>
            Search agencies
            <div style={{position:"relative"}}>
              <input
                data-testid="supplier-search-input"
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Name, specialty, city…"
                style={{width:"100%",padding:"8px 32px 8px 10px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13,background:"#fff",color:"#111827",fontWeight:500,textTransform:"none",letterSpacing:"normal"}}
              />
              {search && (
                <button
                  data-testid="supplier-search-clear"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                  style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",background:"transparent",border:"none",cursor:"pointer",color:"#6B7280",fontSize:16,padding:"0 4px",lineHeight:1}}
                >×</button>
              )}
            </div>
          </label>
          <label style={{display:"flex",flexDirection:"column",gap:4,fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"0.05em",textTransform:"uppercase",minWidth:220}}>
            Sort by
            <select
              data-testid="supplier-sort-select"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              style={{padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13,background:"#fff",color:"#111827",fontWeight:500,textTransform:"none",letterSpacing:"normal"}}
            >
              <option value="tier">Tier (Verified first)</option>
              <option value="name_asc">Name (A–Z)</option>
              <option value="name_desc">Name (Z–A)</option>
              <option value="recent">Recently joined</option>
              <option value="rate_asc">Rate · Low to High</option>
              <option value="rate_desc">Rate · High to Low</option>
              <option value="capability">Capability</option>
            </select>
          </label>
        </div>
      </div>
      <p style={{fontSize:12,color:"#6B7280",margin:"-6px 0 14px"}} data-testid="supplier-count">
        Showing <strong style={{color:"#111827"}}>{suppliersFiltered.length}</strong>{q ? ` of ${suppliersAll.length}` : ""} agenc{suppliersFiltered.length === 1 ? "y" : "ies"}
        {q ? <> matching "<em style={{color:"#111827",fontStyle:"normal",fontWeight:600}}>{search}</em>"</> : null}
        {" · "}sorted by {({tier:"Verified tier",name_asc:"Name A–Z",name_desc:"Name Z–A",recent:"Recently joined",rate_asc:"Rate low → high",rate_desc:"Rate high → low",capability:"Capability"})[sortBy]}
      </p>
      {q && suppliersFiltered.length === 0 && (
        <div data-testid="supplier-empty-state" style={{padding:"28px 24px",textAlign:"center",background:"#F9FAFB",border:"1px dashed #D1D5DB",borderRadius:10,color:"#6B7280",fontSize:13,marginBottom:16}}>
          No verified agencies match "<strong style={{color:"#111827"}}>{search}</strong>". Try another name or specialty — or{" "}
          <button
            data-testid="supplier-empty-clear"
            onClick={() => setSearch("")}
            style={{background:"transparent",border:"none",color:"#2563EB",cursor:"pointer",fontSize:13,padding:0,textDecoration:"underline"}}
          >clear the search</button>.
        </div>
      )}
      {featured && (() => {
        const fRates = rateEntries(featured);
        const fCanHire = role === "buyer" && featured.is_verified;
        const initials = featured.name.split(" ").map(y => y[0]).join("").slice(0, 2);
        return (
          <div
            data-testid={`featured-agency-${featured.id}`}
            style={{
              position:"relative",
              background:"linear-gradient(135deg,#0F172A 0%,#1E3A8A 55%,#2563EB 100%)",
              color:"#F8FAFC",
              padding:"28px 32px",
              borderRadius:16,
              marginBottom:22,
              display:"grid",
              gridTemplateColumns:"auto 1fr auto",
              gap:24,
              alignItems:"center",
              overflow:"hidden",
              boxShadow:"0 20px 45px -25px rgba(30, 64, 175, 0.6)",
            }}
          >
            <span
              style={{
                position:"absolute",top:14,right:16,
                display:"inline-flex",alignItems:"center",gap:6,
                background:"rgba(250, 204, 21, 0.18)",
                color:"#FDE68A",
                padding:"4px 10px",borderRadius:999,fontSize:10,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",
                border:"1px solid rgba(250, 204, 21, 0.5)",
              }}
              data-testid="featured-agency-badge"
              title={featuredIsNominated ? (featured.featured_note || "Admin pick") : "Automatically picked by rating"}
            >
              <Star size={11} fill="#FDE68A" strokeWidth={0}/> {featuredIsNominated ? "Admin pick · featured" : "Featured · top-rated"}
            </span>
            <div
              style={{
                width:88,height:88,borderRadius:22,
                background:"rgba(255,255,255,0.14)",
                border:"1px solid rgba(255,255,255,0.25)",
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:30,fontWeight:700,letterSpacing:"0.08em",color:"#F8FAFC",
                backdropFilter:"blur(6px)",
              }}
            >{initials}</div>
            <div style={{display:"flex",flexDirection:"column",gap:10,minWidth:0}}>
              <p style={{margin:0,fontSize:10,letterSpacing:"0.18em",textTransform:"uppercase",color:"#BFDBFE",fontWeight:600}}>Spotlight hire · ready today</p>
              <h3 style={{margin:0,fontSize:26,fontWeight:600,fontFamily:"'Fraunces', serif",color:"#F8FAFC",lineHeight:1.1}}>
                {featured.name}
              </h3>
              <div style={{display:"flex",flexWrap:"wrap",gap:10,alignItems:"center",fontSize:12,color:"#CBD5F5"}}>
                <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
                  <Star size={12} fill="#FACC15" strokeWidth={0}/> {Number(featured.quality_rating || 4.8).toFixed(1)}
                </span>
                <span>·</span>
                <span>{featured.location?.city || "Remote"}</span>
                {featured.design_tier && (<>
                  <span>·</span>
                  <span style={{display:"inline-flex",alignItems:"center",gap:4,fontWeight:600,color:"#FDE68A"}}>
                    <ShieldCheck size={12}/> {tierLabel(featured.design_tier)}
                  </span>
                </>)}
              </div>
              {(featured.design_specialties || []).length > 0 && (
                <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:2}} data-testid="featured-agency-specs">
                  {(featured.design_specialties || []).slice(0, 5).map(code => (
                    <span key={code} style={{background:"rgba(255,255,255,0.14)",color:"#F8FAFC",padding:"4px 10px",borderRadius:999,fontSize:11,fontWeight:500,border:"1px solid rgba(255,255,255,0.18)"}}>
                      {specLabel(code)}
                    </span>
                  ))}
                </div>
              )}
              {(fRates.length > 0) && (
                <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:4}} data-testid="featured-agency-rates">
                  {fRates.slice(0, 3).map(r => (
                    <span key={r.code} style={{background:"#ECFDF5",color:"#065F46",padding:"5px 11px",borderRadius:8,fontSize:12,fontWeight:600}}>
                      {r.label} · ₹{r.inr.toLocaleString("en-IN")}
                    </span>
                  ))}
                  {featured.revision_rate_inr ? (
                    <span style={{background:"#EFF6FF",color:"#1E3A8A",padding:"5px 11px",borderRadius:8,fontSize:12,fontWeight:600}}>
                      Extra revision · ₹{Number(featured.revision_rate_inr).toLocaleString("en-IN")}/round
                    </span>
                  ) : null}
                </div>
              )}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8,alignItems:"stretch",minWidth:180}}>
              {fCanHire ? (
                <button
                  data-testid={`featured-request-hire-${featured.id}`}
                  onClick={() => setQuickHire({ agency: featured, rates: fRates })}
                  style={{
                    background:"#FACC15",color:"#1F2937",border:"none",
                    padding:"12px 18px",borderRadius:10,fontSize:14,fontWeight:700,
                    cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",gap:8,
                    boxShadow:"0 10px 25px -10px rgba(250, 204, 21, 0.9)",
                  }}
                >{fRates.length && Number(featured.revision_rate_inr) > 0 ? "Request hire" : "Request quote"} <Lock size={14}/></button>
              ) : (
                <span style={{fontSize:11,color:"#CBD5F5",textAlign:"center",padding:"6px 10px",background:"rgba(255,255,255,0.08)",borderRadius:8}}>
                  Published rates coming soon
                </span>
              )}
              <button
                data-testid={`featured-view-profile-${featured.id}`}
                onClick={() => setProfileFor(featured)}
                style={{background:"transparent",color:"#F8FAFC",border:"1px solid rgba(255,255,255,0.3)",padding:"10px 16px",borderRadius:10,fontSize:13,fontWeight:500,cursor:"pointer"}}
              >View profile</button>
              {role === "admin" && featuredIsNominated && (
                <button
                  type="button"
                  data-testid={`featured-unfeature-${featured.id}`}
                  onClick={() => setFeatured(featured.id, false, featured.name)}
                  style={{background:"transparent",color:"#FDE68A",border:"1px dashed rgba(250, 204, 21, 0.6)",padding:"8px 14px",borderRadius:10,fontSize:11,fontWeight:600,cursor:"pointer",letterSpacing:"0.05em",textTransform:"uppercase"}}
                >Remove from spotlight</button>
              )}
            </div>
          </div>
        );
      })()}
      {role === "buyer" ? (
        <BuyerAgencyList suppliers={suppliers} rateEntries={rateEntries} specLabel={specLabel} tierLabel={tierLabel}
          onHire={(a, rates) => setQuickHire({ agency: a, rates })} onView={(a) => setProfileFor(a)}/>
      ) : (
      <AdminAgencyTable suppliers={suppliers} stages={stages} tiers={ref.tiers || []} rateEntries={rateEntries} specLabel={specLabel} tierLabel={tierLabel}
        setStage={setStage} setDesignTier={setDesignTier} setFeatured={setFeatured} onView={(a) => setProfileFor(a)}/>
      )}
      {quickHire && (
        <AgencyQuickHireModal
          agency={quickHire.agency}
          rates={quickHire.rates}
          ref_={ref}
          user={user}
          onClose={() => setQuickHire(null)}
          onDone={(brief, amount, isQuote) => {
            setQuickHire(null);
            reload();
            if (brief && isQuote) {
              toast.success(`Quote request sent to ${quickHire.agency.name}`, { description: "You'll be notified when they submit a price. Track it under Design briefs." });
            } else if (brief) {
              toast.success(`Escrow of ₹${Number(amount).toLocaleString("en-IN")} locked · Brief sent to ${quickHire.agency.name}`, {
                description: "Opening Design Services so you can track proofing.",
                duration: 5000,
              });
              onHireSuccess && onHireSuccess(brief);
            }
          }}
        />
      )}
      {profileFor && (
        <AgencyProfileModal
          agencyId={profileFor.id}
          agencyFallback={profileFor}
          role={role}
          onClose={() => setProfileFor(null)}
          onRequestHire={(agency) => {
            setProfileFor(null);
            const rts = rateEntries(agency);
            if (role === "buyer" && agency.is_verified) {
              setQuickHire({ agency, rates: rts });
            }
          }}
          onSaved={() => reload()}
        />
      )}
    </section>
  );
}

// Buyer-side quick-hire for an agency directly from the Supplier Network.
// Creates a fixed-rate brief + opens Razorpay escrow in one go, with the
// price locked to the agency's published rate for the chosen specialty.
export function AgencyQuickHireModal({ agency, rates, ref_, user, onClose, onDone }) {
  // specialty → brief category mapping (BRIEF_CATEGORIES ∩ DESIGN_SPECIALTIES)
  const SPEC_TO_CAT = { packaging_box: "packaging_box", label: "label", logo: "logo", brochure: "brochure", preflight_dtp: "print_correction" };
  const quoteMode = rates.length === 0 || !(Number(agency.revision_rate_inr) > 0);
  const specOptions = quoteMode
    ? Object.keys(SPEC_TO_CAT).map(code => ({ code, label: ref_?.specialties?.find(x => x.code === code)?.label || code.replace(/_/g, " ") }))
    : rates.filter(r => SPEC_TO_CAT[r.code]);
  const [specialty, setSpecialty] = useState(quoteMode ? ((agency.design_specialties || []).find(c => SPEC_TO_CAT[c]) || "packaging_box") : (rates[0]?.code || ""));
  const [budget, setBudget] = useState("");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [days, setDays] = useState("7");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const picked = rates.find(r => r.code === specialty);
  const price = picked?.inr || 0;
  const category = SPEC_TO_CAT[specialty];
  const canSubmit = !!specialty && !!category && title.trim().length >= 3 && (quoteMode ? Number(budget) > 0 : price > 0) && !busy;

  const submitQuote = async () => {
    setBusy(true); setErr("");
    try {
      const brief = await axios.post(`${API}/design/briefs`, {
        title: title.trim(), category, service_type: "custom", hire_mode: "bidding",
        preferred_specialty: specialty, brand_guidelines: desc.trim() || null,
        budget_inr: Number(budget), deadline_days: Number(days) || null, invited_agency_id: agency.id,
      }).then(r => r.data);
      onDone(brief, 0, true);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(Array.isArray(d) ? d[0]?.msg : d || "Could not send the quote request.");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!canSubmit) return;
    if (quoteMode) return submitQuote();
    if (!window.confirm(`Hire ${agency.name} at a fixed price of ₹${price.toLocaleString("en-IN")} (5 free revisions, then ₹${Number(agency.revision_rate_inr).toLocaleString("en-IN")}/revision)? The full order value will be locked in escrow immediately.`)) return;
    setBusy(true); setErr("");
    try {
      const brief = await axios.post(`${API}/design/briefs`, {
        title: title.trim(),
        category,
        service_type: "custom",
        hire_mode: "fixed",
        preferred_specialty: specialty,
        preferred_tier: agency.design_tier || null,
        brand_guidelines: desc.trim() || null,
        budget_inr: price,
        deadline_days: Number(days) || null,
      }).then(r => r.data);
      const [cfg, order] = await Promise.all([
        axios.get(`${API}/razorpay/config`).then(r => r.data),
        axios.post(`${API}/design/briefs/${brief.id}/hire`, { agency_id: agency.id, price_inr: price }).then(r => r.data),
      ]);
      const resp = await openRazorpayCheckout({
        keyId: cfg.key_id,
        order,
        name: "GoPrintPack · Design escrow",
        description: `${brief.title} · ${agency.name}`,
        prefill: { name: user?.name || "", email: user?.email || "", contact: user?.phone || "" },
        notes: { brief_id: brief.id, agency_id: agency.id, flow: "fixed_hire_quick" },
      });
      await axios.post(`${API}/razorpay/verify`, resp);
      onDone(brief, price);
    } catch (e) {
      if (e?.code === "USER_CANCELLED") setErr("Payment cancelled. Escrow not locked. Brief is still on the Design Services board.");
      else setErr(e?.description || e?.response?.data?.detail || "Could not create brief / open checkout.");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-shell" data-testid="quick-hire-modal" onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:24}}>
      <div onClick={e => e.stopPropagation()} style={{background:"#fff",padding:28,borderRadius:14,width:560,maxWidth:"95vw",maxHeight:"92vh",overflow:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"start",marginBottom:10}}>
          <div>
            <h3 style={{margin:0}}>{quoteMode ? "Request quote" : "Request hire"} · {agency.name}</h3>
            <p style={{margin:"6px 0 0",fontSize:12,color:"#6b7280"}}>{quoteMode
              ? "This agency quotes per project. Share your brief and budget — they'll reply with a fixed project price and paid-revision rate. You pay into escrow only when you accept."
              : "Full order value locks in escrow immediately. Released only after you approve the final artwork."}</p>
          </div>
          <span className={`dtier-badge dtier-${agency.design_tier}`} style={{whiteSpace:"nowrap"}}><ShieldCheck size={11}/> {ref_?.tiers?.find(t => t.code === agency.design_tier)?.label || agency.design_tier || "Verified"}</span>
        </div>

        <div style={{display:"grid",gap:12,marginTop:14}}>
          <label style={{fontSize:12,color:"#6b7280"}}>Service type
            <select data-testid="qh-specialty" value={specialty} onChange={e => setSpecialty(e.target.value)} style={{width:"100%",padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,marginTop:4,fontSize:13}}>
              {specOptions.map(r => (
                <option key={r.code} value={r.code}>{r.label}{r.inr ? ` — ₹${r.inr.toLocaleString("en-IN")}` : ""}</option>
              ))}
            </select>
            {!category && specialty && (
              <span style={{fontSize:11,color:"#B91C1C"}}>No brief category maps to this specialty yet — pick another or post a full brief.</span>
            )}
          </label>

          {quoteMode ? (
            <label style={{fontSize:12,color:"#6b7280"}}>Your budget (INR) *
              <input data-testid="qh-budget" type="number" min="1" value={budget} onChange={e => setBudget(e.target.value)} placeholder="e.g. 5000" style={{width:"100%",padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,marginTop:4,fontSize:13}}/>
            </label>
          ) : (
            <div style={{background:"#F0FDF4",border:"1px solid #86EFAC",padding:12,borderRadius:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontSize:12,color:"#065F46"}}>Escrow amount <span style={{opacity:0.7}}>(locked) · 5 free revisions, then ₹{Number(agency.revision_rate_inr).toLocaleString("en-IN")}/round</span></div>
              <div data-testid="qh-price" style={{fontSize:20,fontWeight:700,color:"#065F46"}}>₹{price.toLocaleString("en-IN")}</div>
            </div>
          )}

          <label style={{fontSize:12,color:"#6b7280"}}>Project title *
            <input data-testid="qh-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. New line of chocolate boxes for Diwali" maxLength={140} style={{width:"100%",padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,marginTop:4,fontSize:13}}/>
          </label>

          <label style={{fontSize:12,color:"#6b7280"}}>Brief / brand guidelines (optional)
            <textarea data-testid="qh-desc" value={desc} onChange={e => setDesc(e.target.value)} rows={3} placeholder="Palette, references, do's and don'ts…" style={{width:"100%",padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,marginTop:4,fontSize:13,fontFamily:"inherit"}}/>
          </label>

          <label style={{fontSize:12,color:"#6b7280"}}>Deadline (days)
            <input data-testid="qh-days" type="number" min="1" max="90" value={days} onChange={e => setDays(e.target.value)} style={{width:140,padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,marginTop:4,fontSize:13}}/>
          </label>
        </div>

        {err && <div className="form-error" data-testid="qh-error" style={{marginTop:10,color:"#B91C1C",fontSize:12}}>{err}</div>}

        <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:18}}>
          <button data-testid="qh-cancel" className="secondary-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button data-testid="qh-submit" className="primary-btn" disabled={!canSubmit} onClick={submit}>
            {quoteMode ? (busy ? "Sending…" : "Send quote request") : busy ? "Opening Razorpay…" : <>Fund escrow & hire <Lock size={13}/></>}
          </button>
        </div>
      </div>
    </div>
  );
}

// Buyer-facing agency profile viewer with admin edit mode.
// Admins can edit a curated set of profile fields (tagline/about/team/tools/…).
// All text fields are server-side scrubbed of emails, URLs and phone numbers so
// the public profile never exposes a direct contact channel between buyer and
// agency — all engagement must flow through briefs + escrow.
export function AgencyProfileModal({ agencyId, agencyFallback, role, onClose, onRequestHire, onSaved }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [designRef, setDesignRef] = useState({ specialties: [] });
  const isAdmin = role === "admin";
  const isBuyer = role === "buyer";

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get(`${API}/agencies/${agencyId}/profile`)
      .then(r => { if (active) setProfile(r.data); })
      .catch(() => { if (active) setProfile(null); })
      .finally(() => { if (active) setLoading(false); });
    axios.get(`${API}/design/reference`)
      .then(r => { if (active) setDesignRef(r.data || { specialties: [] }); })
      .catch(() => {});
    return () => { active = false; };
  }, [agencyId]);

  const startEdit = () => {
    const p = profile || {};
    const fr = p.fixed_rates || {};
    setForm({
      tagline: p.tagline || "",
      about: p.about || "",
      established_year: p.established_year || "",
      team_size: p.team_size || "",
      tools: (p.tools || []).join(", "),
      industries: (p.industries || []).join(", "),
      languages: (p.languages || []).join(", "),
      min_engagement_inr: p.min_engagement_inr ?? "",
      typical_turnaround_days: p.typical_turnaround_days ?? "",
      revisions_included: p.revisions_included ?? "",
      response_sla_hours: p.response_sla_hours ?? "",
      revision_rate_inr: p.revision_rate_inr ?? "",
      fixed_rate_rows: Object.entries(fr).map(([code, inr]) => ({ code, inr })),
    });
    setErr("");
    setEditing(true);
  };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const toList = (s) => (s || "").split(",").map(x => x.trim()).filter(Boolean);
      const toFixedRates = (list) => {
        const map = {};
        for (const row of (list || [])) {
          const code = (row.code || "").trim();
          const n = Number(row.inr);
          if (code && n > 0) map[code] = n;
        }
        return map;
      };
      const payload = {
        tagline: form.tagline || null,
        about: form.about || null,
        established_year: form.established_year === "" ? null : Number(form.established_year),
        team_size: form.team_size || null,
        tools: toList(form.tools),
        industries: toList(form.industries),
        languages: toList(form.languages),
        min_engagement_inr: form.min_engagement_inr === "" ? null : Number(form.min_engagement_inr),
        typical_turnaround_days: form.typical_turnaround_days === "" ? null : Number(form.typical_turnaround_days),
        revisions_included: form.revisions_included === "" ? null : Number(form.revisions_included),
        response_sla_hours: form.response_sla_hours === "" ? null : Number(form.response_sla_hours),
        revision_rate_inr: form.revision_rate_inr === "" ? null : Number(form.revision_rate_inr),
        fixed_rates: toFixedRates(form.fixed_rate_rows),
      };
      await axios.patch(`${API}/admin/agencies/${agencyId}/profile`, payload);
      const r = await axios.get(`${API}/agencies/${agencyId}/profile`);
      setProfile(r.data);
      toast.success("Agency profile updated");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save profile.");
    } finally { setSaving(false); }
  };

  const p = profile || agencyFallback || {};
  const name = p.name || agencyFallback?.name || "Agency";
  const initials = name.split(" ").map(y => y[0]).join("").slice(0, 2);
  const specLabels = p.design_specialty_labels || p.design_specialties || [];
  const fixedRates = Object.entries(p.fixed_rates || {});
  const canHireFromProfile = isBuyer && p.is_verified;
  const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  return (
    <div
      data-testid="agency-profile-modal"
      onClick={onClose}
      style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:24}}
    >
      <div onClick={e => e.stopPropagation()} style={{background:"#fff",borderRadius:16,width:720,maxWidth:"96vw",maxHeight:"94vh",overflow:"auto",boxShadow:"0 25px 70px -20px rgba(15,23,42,0.5)"}}>
        <div style={{padding:"24px 28px 20px",background:"linear-gradient(135deg,#0F172A 0%,#1E3A8A 100%)",color:"#F8FAFC",display:"grid",gridTemplateColumns:"72px 1fr auto",gap:18,alignItems:"center",borderRadius:"16px 16px 0 0"}}>
          <div style={{width:72,height:72,borderRadius:18,background:"rgba(255,255,255,0.14)",border:"1px solid rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,fontWeight:700,letterSpacing:"0.08em"}}>{initials}</div>
          <div style={{minWidth:0}}>
            <h2 style={{margin:"0 0 4px",fontFamily:"'Fraunces', serif",fontSize:24,fontWeight:600,color:"#F8FAFC"}} data-testid="agency-profile-name">{name}</h2>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,alignItems:"center",fontSize:12,color:"#CBD5F5"}}>
              {p.is_verified ? (<span style={{display:"inline-flex",alignItems:"center",gap:4,color:"#86EFAC"}}><CheckCircle2 size={12}/> Verified</span>) : (<span style={{color:"#FDE68A"}}>{p.verification_stage || "Pending verification"}</span>)}
              {p.design_tier_label && (<>
                <span>·</span>
                <span style={{display:"inline-flex",alignItems:"center",gap:4,color:"#FDE68A",fontWeight:600}}><ShieldCheck size={12}/> {p.design_tier_label}</span>
              </>)}
              {p.quality_rating ? (<>
                <span>·</span>
                <span style={{display:"inline-flex",alignItems:"center",gap:4}} data-testid="agency-profile-rating">
                  <span style={{display:"inline-flex",gap:1}}>{[1,2,3,4,5].map(i => <span key={i} style={{color: i <= Math.round(Number(p.quality_rating)) ? "#FACC15" : "rgba(255,255,255,0.25)",fontSize:14,lineHeight:1}}>★</span>)}</span>
                  <b>{Number(p.quality_rating).toFixed(1)}</b>
                  {p.rating_count ? <span style={{color:"#CBD5E1"}}>· {p.rating_count} review{p.rating_count === 1 ? "" : "s"}</span> : <span style={{color:"#94A3B8"}}>· new</span>}
                </span>
              </>) : null}
              {p.location?.city && (<><span>·</span><span>{p.location.city}{p.location.state ? `, ${p.location.state}` : ""}</span></>)}
            </div>
            {p.tagline && !editing && (
              <p data-testid="agency-profile-tagline" style={{margin:"8px 0 0",fontSize:13,color:"#E0E7FF",fontStyle:"italic"}}>{p.tagline}</p>
            )}
          </div>
          <button onClick={onClose} aria-label="Close" style={{background:"rgba(255,255,255,0.12)",color:"#F8FAFC",border:"none",borderRadius:999,width:32,height:32,cursor:"pointer",fontSize:18,lineHeight:1}}>×</button>
        </div>

        <div style={{padding:"24px 28px 28px"}}>
          {loading ? (
            <p style={{textAlign:"center",color:"#6B7280",fontSize:13,padding:"40px 0"}}>Loading profile…</p>
          ) : !editing ? (
            <ProfileReadView p={p} specLabels={specLabels} fixedRates={fixedRates} money={money}/>
          ) : (
            <ProfileEditView form={form} setForm={setForm} err={err} designRef={designRef}/>
          )}

          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:22,paddingTop:18,borderTop:"1px solid #E5E7EB",flexWrap:"wrap",gap:12}}>
            <p style={{margin:0,fontSize:11,color:"#6B7280"}}>All engagement is routed through briefs + escrow. Direct contact info is not shown on profiles.</p>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              {editing ? (
                <>
                  <button data-testid="agency-profile-cancel" onClick={() => setEditing(false)} disabled={saving} style={{background:"#F3F4F6",color:"#374151",border:"1px solid #D1D5DB",padding:"9px 16px",borderRadius:8,fontSize:13,fontWeight:500,cursor:"pointer"}}>Cancel</button>
                  <button data-testid="agency-profile-save" onClick={save} disabled={saving} style={{background:"#2563EB",color:"#fff",border:"none",padding:"9px 18px",borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer"}}>{saving ? "Saving…" : "Save profile"}</button>
                </>
              ) : (
                <>
                  {isAdmin && (
                    <button data-testid="agency-profile-edit" onClick={startEdit} style={{background:"#F3F4F6",color:"#111827",border:"1px solid #D1D5DB",padding:"9px 16px",borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer"}}>Edit profile</button>
                  )}
                  {canHireFromProfile && (
                    <button
                      data-testid="agency-profile-request-hire"
                      onClick={() => onRequestHire && onRequestHire(p)}
                      style={{background:"#111827",color:"#FACC15",border:"none",padding:"10px 20px",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:8}}
                    >Request hire <Lock size={13}/></button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProfileReadView({ p, specLabels, fixedRates, money }) {
  const row = (label, value) => (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,padding:"8px 0",borderBottom:"1px dashed #E5E7EB",fontSize:13}}>
      <span style={{color:"#6B7280",fontWeight:500}}>{label}</span>
      <span style={{color:"#111827",fontWeight:600,textAlign:"right"}}>{value || "—"}</span>
    </div>
  );
  const chipRow = (items, color = "#111827", bg = "#F3F4F6") => (
    <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:8}}>
      {items.length ? items.map((t, i) => (
        <span key={i} style={{background:bg,color,padding:"4px 10px",borderRadius:999,fontSize:11,fontWeight:600,border:"1px solid #E5E7EB"}}>{t}</span>
      )) : <span style={{fontSize:12,color:"#9CA3AF"}}>Not published yet</span>}
    </div>
  );
  return (
    <div style={{display:"grid",gap:22}}>
      {p.about && (
        <section data-testid="agency-profile-about">
          <h3 style={{margin:"0 0 8px",fontSize:11,letterSpacing:"0.14em",textTransform:"uppercase",color:"#6B7280",fontWeight:700}}>About the studio</h3>
          <p style={{margin:0,color:"#111827",fontSize:14,lineHeight:1.55}}>{p.about}</p>
        </section>
      )}

      <section>
        <h3 style={{margin:"0 0 8px",fontSize:11,letterSpacing:"0.14em",textTransform:"uppercase",color:"#6B7280",fontWeight:700}}>Studio snapshot</h3>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 24px"}}>
          {row("Established", p.established_year)}
          {row("Team size", p.team_size)}
        </div>
      </section>

      <section>
        <h3 style={{margin:"0 0 4px",fontSize:11,letterSpacing:"0.14em",textTransform:"uppercase",color:"#6B7280",fontWeight:700}}>Specialties</h3>
        {chipRow(specLabels, "#065F46", "#ECFDF5")}
      </section>

      <section data-testid="agency-profile-tools">
        <h3 style={{margin:"0 0 4px",fontSize:11,letterSpacing:"0.14em",textTransform:"uppercase",color:"#6B7280",fontWeight:700}}>Software &amp; tools</h3>
        {chipRow(p.tools || [])}
      </section>

      <section data-testid="agency-profile-industries">
        <h3 style={{margin:"0 0 4px",fontSize:11,letterSpacing:"0.14em",textTransform:"uppercase",color:"#6B7280",fontWeight:700}}>Industries served</h3>
        {chipRow(p.industries || [], "#1E3A8A", "#EFF6FF")}
      </section>

      <section data-testid="agency-profile-languages">
        <h3 style={{margin:"0 0 4px",fontSize:11,letterSpacing:"0.14em",textTransform:"uppercase",color:"#6B7280",fontWeight:700}}>Languages</h3>
        {chipRow(p.languages || [], "#78350F", "#FEF3C7")}
      </section>

      <section>
        <h3 style={{margin:"0 0 8px",fontSize:11,letterSpacing:"0.14em",textTransform:"uppercase",color:"#6B7280",fontWeight:700}}>Pricing &amp; delivery promise</h3>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 24px"}}>
          {row("Paid revision rate", p.revision_rate_inr ? `${money(p.revision_rate_inr)}/round (after 5 free)` : null)}
          {row("Minimum engagement", p.min_engagement_inr ? money(p.min_engagement_inr) : null)}
          {row("Typical turnaround", p.typical_turnaround_days ? `${p.typical_turnaround_days} days` : null)}
          {row("Revisions included", p.revisions_included != null ? `${p.revisions_included} rounds` : null)}
          {row("Response SLA", p.response_sla_hours ? `${p.response_sla_hours} hours` : null)}
          {row("Fixed rates", fixedRates.length ? `${fixedRates.length} published` : null)}
        </div>
        {fixedRates.length > 0 && (
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:10}}>
            {fixedRates.map(([code, inr]) => {
              const idx = (p.design_specialties || []).indexOf(code);
              const label = idx >= 0 && specLabels[idx] ? specLabels[idx] : code;
              return (
                <span key={code} style={{background:"#ECFDF5",color:"#065F46",padding:"5px 11px",borderRadius:8,fontSize:12,fontWeight:600,border:"1px solid #A7F3D0"}}>
                  {label} · {money(inr)}
                </span>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

export function ProfileEditView({ form, setForm, err, designRef }) {
  const bind = (key) => ({ value: form[key] ?? "", onChange: (e) => setForm({ ...form, [key]: e.target.value }) });
  const inputStyle = { width:"100%", padding:"8px 10px", border:"1px solid #D1D5DB", borderRadius:8, fontSize:13, marginTop:4 };
  const labelStyle = { fontSize:11, color:"#6B7280", fontWeight:600, letterSpacing:"0.08em", textTransform:"uppercase" };
  const hint = (t) => <span style={{display:"block",marginTop:3,fontSize:10,color:"#9CA3AF",fontWeight:400,letterSpacing:"normal",textTransform:"none"}}>{t}</span>;
  const specialties = designRef?.specialties || [];
  const rows = form.fixed_rate_rows || [];
  const usedCodes = new Set(rows.map(r => r.code).filter(Boolean));
  const availableSpecialties = specialties.filter(s => !usedCodes.has(s.code));
  const updateRow = (idx, patch) => {
    const next = rows.map((r, i) => i === idx ? { ...r, ...patch } : r);
    setForm({ ...form, fixed_rate_rows: next });
  };
  const removeRow = (idx) => setForm({ ...form, fixed_rate_rows: rows.filter((_, i) => i !== idx) });
  const addRow = () => {
    if (!availableSpecialties.length) return;
    setForm({ ...form, fixed_rate_rows: [...rows, { code: availableSpecialties[0].code, inr: "" }] });
  };
  return (
    <div style={{display:"grid",gap:16}} data-testid="agency-profile-edit-form">
      {err && <div style={{background:"#FEF2F2",border:"1px solid #FECACA",color:"#991B1B",padding:"8px 12px",borderRadius:8,fontSize:12}}>{err}</div>}

      <h3 style={{margin:0,fontSize:11,letterSpacing:"0.14em",textTransform:"uppercase",color:"#6B7280",fontWeight:700}}>About the studio</h3>
      <label style={labelStyle}>Tagline
        <input data-testid="profile-edit-tagline" maxLength={160} {...bind("tagline")} placeholder="Packaging for F&B brands who care about shelf-presence" style={inputStyle}/>
        {hint("Public one-liner. Emails, URLs and phone numbers will be auto-removed.")}
      </label>
      <label style={labelStyle}>About (max 600 chars)
        <textarea data-testid="profile-edit-about" maxLength={600} rows={4} {...bind("about")} placeholder="How the studio works, who they serve, what makes them different…" style={{...inputStyle,fontFamily:"inherit",resize:"vertical"}}/>
        {hint("No contact channels please — all buyer–agency communication must go through briefs.")}
      </label>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <label style={labelStyle}>Established year
          <input data-testid="profile-edit-year" type="number" min={1900} max={2100} {...bind("established_year")} placeholder="2016" style={inputStyle}/>
        </label>
        <label style={labelStyle}>Team size
          <input data-testid="profile-edit-team" maxLength={40} {...bind("team_size")} placeholder="e.g. 2–5, 6–15, 16–50" style={inputStyle}/>
        </label>
      </div>

      <h3 style={{margin:"6px 0 0",fontSize:11,letterSpacing:"0.14em",textTransform:"uppercase",color:"#6B7280",fontWeight:700}}>Capabilities &amp; focus</h3>
      <label style={labelStyle}>Software &amp; tools
        <input data-testid="profile-edit-tools" {...bind("tools")} placeholder="Figma, Illustrator, Esko ArtPro, InDesign" style={inputStyle}/>
        {hint("Comma-separated. Max 24 entries, 48 chars each.")}
      </label>
      <label style={labelStyle}>Industries served
        <input data-testid="profile-edit-industries" {...bind("industries")} placeholder="F&B, Beauty, Pharma, Electronics" style={inputStyle}/>
        {hint("Comma-separated.")}
      </label>
      <label style={labelStyle}>Languages
        <input data-testid="profile-edit-languages" {...bind("languages")} placeholder="English, Hindi, Marathi, Tamil" style={inputStyle}/>
        {hint("Comma-separated.")}
      </label>

      <h3 style={{margin:"6px 0 0",fontSize:11,letterSpacing:"0.14em",textTransform:"uppercase",color:"#6B7280",fontWeight:700}}>Pricing &amp; delivery promise</h3>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <label style={labelStyle}>Minimum engagement (INR)
          <input data-testid="profile-edit-min" type="number" min={0} {...bind("min_engagement_inr")} placeholder="15000" style={inputStyle}/>
        </label>
        <label style={labelStyle}>Typical turnaround (days)
          <input data-testid="profile-edit-turnaround" type="number" min={0} max={365} {...bind("typical_turnaround_days")} placeholder="7" style={inputStyle}/>
        </label>
        <label style={labelStyle}>Revisions included
          <input data-testid="profile-edit-revisions" type="number" min={0} max={99} {...bind("revisions_included")} placeholder="3" style={inputStyle}/>
        </label>
        <label style={labelStyle}>Response SLA (hours)
          <input data-testid="profile-edit-sla" type="number" min={0} max={720} {...bind("response_sla_hours")} placeholder="12" style={inputStyle}/>
        </label>
      </div>
      <h3 style={{margin:"6px 0 0",fontSize:11,letterSpacing:"0.14em",textTransform:"uppercase",color:"#6B7280",fontWeight:700}}>Rate card</h3>
      <label style={labelStyle}>Paid revision rate (INR / round)
        <input data-testid="profile-edit-revision-rate" type="number" min={0} {...bind("revision_rate_inr")} placeholder="500" style={inputStyle}/>
        {hint("Price per extra revision round after the 5 free revisions. Required for instant hire.")}
      </label>
      <div data-testid="profile-edit-fixed-rates">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
          <span style={labelStyle}>Fixed rates per specialty</span>
          <button
            type="button"
            data-testid="profile-edit-fixed-add"
            onClick={addRow}
            disabled={!availableSpecialties.length}
            style={{background:"#111827",color:"#fff",border:"none",padding:"6px 12px",borderRadius:6,fontSize:11,fontWeight:600,cursor: availableSpecialties.length ? "pointer" : "not-allowed",opacity: availableSpecialties.length ? 1 : 0.4}}
          >+ Add rate</button>
        </div>
        {rows.length === 0 ? (
          <p style={{fontSize:12,color:"#9CA3AF",margin:0}}>No fixed rates yet. Click "+ Add rate" to publish per-specialty prices.</p>
        ) : (
          <div style={{display:"grid",gap:8}}>
            {rows.map((row, idx) => {
              const otherUsed = new Set(rows.filter((_, i) => i !== idx).map(r => r.code).filter(Boolean));
              const options = specialties.filter(s => !otherUsed.has(s.code) || s.code === row.code);
              return (
                <div key={idx} style={{display:"grid",gridTemplateColumns:"1fr 140px auto",gap:8,alignItems:"center"}}>
                  <select
                    data-testid={`profile-edit-fixed-spec-${idx}`}
                    value={row.code || ""}
                    onChange={e => updateRow(idx, { code: e.target.value })}
                    style={{...inputStyle, marginTop:0}}
                  >
                    <option value="">— pick specialty —</option>
                    {options.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
                  </select>
                  <input
                    data-testid={`profile-edit-fixed-inr-${idx}`}
                    type="number"
                    min={0}
                    value={row.inr ?? ""}
                    onChange={e => updateRow(idx, { inr: e.target.value })}
                    placeholder="INR"
                    style={{...inputStyle, marginTop:0}}
                  />
                  <button
                    type="button"
                    data-testid={`profile-edit-fixed-remove-${idx}`}
                    onClick={() => removeRow(idx)}
                    aria-label="Remove rate"
                    style={{background:"transparent",border:"1px solid #D1D5DB",borderRadius:6,width:32,height:36,cursor:"pointer",color:"#991B1B",fontSize:16,lineHeight:1}}
                  >×</button>
                </div>
              );
            })}
          </div>
        )}
        {hint(`${specialties.length - rows.length} specialt${(specialties.length - rows.length) === 1 ? "y" : "ies"} remaining. Specialty list is managed under the Supplier Network → Design tier dropdown.`)}
      </div>
      <p style={{margin:0,fontSize:11,color:"#6B7280"}}>Specialties and design tier are managed on the Supplier Network card. Rates saved here mirror directly onto the public agency profile.</p>
    </div>
  );
}
